import { Input, Select } from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { ErrorBoundary } from '@sentry/react'
import { AboveAllModal } from 'components/AboveAllModalOverlay/AboveAllModalOverlay'
import { FIXED_CHILDREN_MODAL_TRANSITION } from 'components/AboveAllModalOverlay/fixedChildrenModalTransition'
import { WideHeader } from 'components/ModalDrawer/WideHeader/WideHeader'
import { translate } from 'i18n/i18n'
import { cloneDeep } from 'lodash'
import { useState } from 'react'
import { Checkbox } from '../../../components/Checkbox/Checkbox'
import { SharedSettings } from '../../../settings/SharedSettings'
import { ACTION_MENU_MODAL_ID } from '../ActionMenu/ActionMenu'
import { Footer } from '../NodeInstallation/Footer/Footer'
import { NodeInfoHeader } from '../Shared/NodeInfoHeader/NodeInfoHeader'
import { serviceCenterStore } from '../store/serviceCenterStore'
import type { TActiveGateway, TActiveNode } from '../types'
import { createErrorModal } from '../utils/createCommonModals'
import { removeGateway } from '../utils/removeGateway'
import { removeNode } from '../utils/removeNode'

const MODAL_ID = 'node-remove-modal'

export const openNodeRemoveModal = (node: TActiveNode | TActiveGateway, onClose?: () => void) => {
  AboveAllModal.open({
    modalId: MODAL_ID,
    fullScreen: true,
    withCloseButton: false,
    padding: 0,
    transitionProps: FIXED_CHILDREN_MODAL_TRANSITION,
    children: (
      <ErrorBoundary>
        <NodeRemoveModal
          node={cloneDeep(node)}
          onClose={() => {
            onClose && onClose()

            AboveAllModal.close(MODAL_ID)
          }}
        />
      </ErrorBoundary>
    ),
    styles: {
      content: {
        marginLeft: 'env(safe-area-inset-left)',
        marginRight: 'env(safe-area-inset-right)',
        boxShadow: 'none',
      },
    },
  })
}

const NodeRemoveModal = ({ node, onClose }: { node: TActiveNode | TActiveGateway; onClose: () => void }) => {
  const [isSaving, setIsSaving] = useState(false)
  const selectedNode = node
  const isGateway = !!(selectedNode as TActiveGateway)?.gatewayType
  const selectedNodePropertyId = (selectedNode as TActiveNode).propertyId

  const selectedNodeIdentifier =
    (selectedNode as TActiveNode).nodeIdentifier || (selectedNode as TActiveGateway).gatewayIdentifier

  const removeReasons = serviceCenterStore.useSelector((s) =>
    serviceCenterStore.selectors.getRemoveReasonsBySelectedNodeType(
      s,
      (node as TActiveNode)?.nodeType || (node as TActiveGateway)?.gatewayType,
    ),
  )

  const form = useForm({
    initialValues: {
      removeReason: '',
      replan: false,
    },
    validate: {
      removeReason: (value) => {
        if (!value) return translate.phrases.placeholder('Required')
      },
    },
  })

  const handleSubmit = async () => {
    const { hasErrors } = form.validate()

    if (hasErrors) return

    setIsSaving(true)

    const { removeReason, replan } = form.values

    try {
      if (isGateway) {
        const payload = {
          gatewayIdentifier: selectedNode?.gatewayIdentifier as string,
          reason: removeReason,
          propertyId: (selectedNode as TActiveGateway)?.propertyIds[0],
        }

        await removeGateway(payload)
      } else {
        const payload = {
          nodeIdentifier: selectedNodeIdentifier,
          reason: removeReason,
          propertyId: selectedNodePropertyId,
        }

        await removeNode(payload, replan)
      }

      notifications.show({
        title: translate.phrases.placeholder('Remove Node'),
        message: translate.phrases.placeholder('Node has been removed successfully.'),
      })

      AboveAllModal.close(ACTION_MENU_MODAL_ID)

      onClose()
    } catch (e) {
      AboveAllModal.open(createErrorModal(translate.phrases.placeholder('Error'), (e as Error).message))
    }

    setIsSaving(false)
  }

  return (
    <>
      <WideHeader onClose={onClose} title={translate.phrases.placeholder('Remove Equipment')} />

      <div css={{ padding: '16px' }}>
        <NodeInfoHeader node={node} />

        <Input.Wrapper id="reason" label={translate.phrases.placeholder('Reason')} css={{ marginTop: 20 }}>
          <Select
            data={removeReasons.map((item) => ({ label: item.removeReason, value: item.removeId }))}
            {...form.getInputProps('removeReason')}
            styles={SharedSettings.MANTINE_SELECT_RIGHT_ICON_CHANGER}
            placeholder={translate.phrases.placeholder('Select reason for removal')}
          />
        </Input.Wrapper>

        {!isGateway && (
          <Input.Wrapper id="replan" label={translate.phrases.placeholder('Re-plan')} css={{ marginTop: 20 }}>
            <Checkbox
              label={translate.phrases.placeholder(
                'Keep a copy of this equipment with the same configuration for later installation at this location.',
              )}
              {...form.getInputProps('replan', { type: 'checkbox' })}
            />
          </Input.Wrapper>
        )}
      </div>

      <Footer
        onPrevious={onClose}
        onNext={handleSubmit}
        nextButtonLabel={translate.phrases.placeholder('Submit')}
        disableNextButton={isSaving}
      />
    </>
  )
}
